import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

// Configure PDF.js worker - use local worker file from public folder
// Ensure public/pdf.worker.min.js matches the pdfjs-dist version (5.3.31)
if (typeof window !== 'undefined') {
    // Use local worker file from public folder
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
}

const getPdfPageCount = async (file) => {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ 
            data: arrayBuffer,
            useSystemFonts: true,
            verbosity: 0 // Suppress warnings
        });
        const pdf = await loadingTask.promise;
        return pdf.numPages;
    } catch (error) {
        console.error("PDF parsing error:", error);
        // Re-throw with more context
        throw new Error(`Failed to parse PDF: ${error.message || 'Unknown error'}`);
    }
};

export const useUserResumeData = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Helper function to calculate file size (mock implementation)
  function calculateFileSize(fileUrl) {
    // Since we don't get file size from API, we'll return a default
    // In a real scenario, you might want to fetch this information
    return "Unknown size";
  }

  // Transform API resume data to match component expectations
  const transformResumeData = useCallback((apiResume) => {
    return {
      id: apiResume.id,
      name: apiResume.original_filename,
      file_url: apiResume.file,
      resume_url: apiResume.resume_url,
      uploaded_at: apiResume.uploaded_at,
      size: calculateFileSize(apiResume.file), // We'll need to calculate this or get from API
      is_active: apiResume.is_active,
      parsed_data: {
        skills: apiResume.parsed_data?.skills || [],
        projects: apiResume.parsed_data?.projects || [],
        experience: apiResume.parsed_data?.experience || [],
        education: apiResume.parsed_data?.education || [],
        raw_text: apiResume.parsed_data?.raw_text || ''
      },
      candidate: apiResume.candidate
    };
  }, []);

  // Fetch all resumes
  const fetchResumes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/resumes/', {
        headers: {
          'Authorization': `Token ${localStorage.getItem('adminToken')}`,
        },
      });
      
      // Transform the API response
      const transformedResumes = response.data.results?.map(transformResumeData) || [];
      setResumes(transformedResumes);
      
    } catch (err) {
      setError(err.message || 'Failed to fetch resumes');
      console.error('Failed to fetch resumes:', err);
    } finally {
      setLoading(false);
    }
  }, [transformResumeData]);

  // Upload new resume
  const uploadResume = async (file) => {
    try {
      setUploadLoading(true);
      setError(null);

      // Create FormData with only the file key as specified
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/resumes/', formData, {
        headers: {
          'Authorization': `Token ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Transform and add the new resume to the list
      const newResume = transformResumeData(response.data);
      
      try {
        await api.post(`/resumes/${newResume.id}/set_active/`);
        newResume.is_active = true;
      } catch (setActiveErr) {
        console.error("Failed to set new resume as active:", setActiveErr);
        // We can choose to show an error to the user here if needed
      }

      setResumes(prev => {
        const updatedResumes = prev.map(r => ({ ...r, is_active: false }));
        return [newResume, ...updatedResumes];
      });
      
      return newResume;
    } catch (err) {
      setError(err.message || 'Failed to upload resume');
      console.error('Failed to upload resume:', err);
      throw err;
    } finally {
      setUploadLoading(false);
    }
  };

  // Delete resume
  const deleteResume = async (resumeId) => {
    try {
      setDeleteLoading(true);
      setError(null);

      await api.delete(`/resumes/${resumeId}/`, {
        headers: {
          'Authorization': `Token ${localStorage.getItem('adminToken')}`,
        },
      });

      // Remove the resume from the local state
      setResumes(prev => prev.filter(resume => resume.id !== resumeId));
      
    } catch (err) {
      setError(err.message || 'Failed to delete resume');
      console.error('Failed to delete resume:', err);
      throw err;
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle resume upload from file input
  const handleResumeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }

    if (file.type === 'application/pdf') {
            try {
                const pageCount = await getPdfPageCount(file);
                if (pageCount > 10) {
                    setSnackbar({
                        open: true,
                        message: 'PDF resumes cannot exceed 10 pages.',
                        severity: 'error'
                    });
                    return;
                }
            } catch (error) {
                console.error("Failed to parse PDF:", error);
                const errorMessage = error.message || 'Unknown error';
                console.error("PDF parsing error details:", errorMessage);
                setSnackbar({
                    open: true,
                    message: `Could not read the PDF file to check the page count. ${errorMessage.includes('worker') ? 'Please refresh the page and try again.' : ''}`,
                    severity: 'error'
                });
                return;
            }
        }

    // Validate file size (e.g., max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      await uploadResume(file);
      // Clear the input value to allow uploading the same file again
      event.target.value = '';
    } catch (error) {
      // Error is already handled in uploadResume
    }
  };

  // Handle resume deletion with confirmation
const handleResumeDelete = async (resumeId) => {
  try {
    await deleteResume(resumeId);
  } catch (error) {
    // Error is already handled in deleteResume
  }
};


  // Fetch resumes on component mount
  useEffect(() => {
    fetchResumes();
  }, [fetchResumes]);

  // Refresh resumes (useful for manual refresh)
  const refreshResumes = () => {
    fetchResumes();
  };

  return {
    resumes,
    setResumes,
    loading,
    error,
    uploadLoading,
    deleteLoading,
    uploadResume,
    deleteResume,
    handleResumeUpload,
    handleResumeDelete,
    refreshResumes,
    setError, // Allow components to clear errors
    snackbar,
    setSnackbar,
  };
};